/**
 * «Посеять без плана» — арифметика мастера. Чистые функции, без React и API.
 *
 * Всё здесь агроном видит цифрами на экране до сохранения: расхождение между
 * контуром и фактически засеянным, потребность в семенах, свободная площадь
 * поля. Поэтому логика лежит отдельно и покрыта тестами.
 */

export type SowStep = 'where' | 'what' | 'how' | 'status';

export const SOW_STEPS: SowStep[] = ['where', 'what', 'how', 'status'];

/** Единица густоты сева. Зависит от культуры, поэтому переключается в форме. */
export type SeedingRateUnit = 'pcs_ha' | 'mln_pcs_ha' | 'kg_ha';

export const SEEDING_RATE_UNITS: Array<{ value: SeedingRateUnit; label: string; hint: string }> = [
  { value: 'pcs_ha', label: 'шт/га', hint: 'пропашные — штук на гектар' },
  { value: 'mln_pcs_ha', label: 'млн шт/га', hint: 'зерновые — миллионов штук на гектар' },
  { value: 'kg_ha', label: 'кг/га', hint: 'часть хозяйств считает густоту весом' },
];

export interface SowDraft {
  /** Зона поля или null — поле целиком (эталонный контур). */
  subzoneId: string | null;
  cropKey: string;
  cropName?: string;
  fallowKind: string | null;
  varietyIds: string[];
  contourAreaHa: number | null;
  areaHa: number | null;
  areaDiffReason: string;
  sowingDate: string | null;
  seedingRate: number | null;
  seedingRateUnit: SeedingRateUnit;
  seedingNormKgHa: number | null;
  seedingDepthCm: number | null;
  alreadySown: boolean;
  sowOperationTypeId: string | null;
  comment: string;
  /** «Это пожнивной / второй посев» — явное намерение, а не двойной клик. */
  isSecondCrop: boolean;
}

export function emptyDraft(): SowDraft {
  return {
    subzoneId: null,
    cropKey: '',
    fallowKind: null,
    varietyIds: [],
    contourAreaHa: null,
    areaHa: null,
    areaDiffReason: '',
    sowingDate: null,
    seedingRate: null,
    seedingRateUnit: 'pcs_ha',
    seedingNormKgHa: null,
    seedingDepthCm: null,
    alreadySown: false,
    sowOperationTypeId: null,
    comment: '',
    isSecondCrop: false,
  };
}

/**
 * Черновик на открытии мастера.
 *
 * Контур «Всё поле» подсвечен как выбранный с первого кадра — значит и площадь
 * обязана быть подставлена с первого кадра. Иначе агроном, ничего не нажимая,
 * жмёт «Дальше», и `area_ha` остаётся пустым до самой факт-операции «Сев»:
 * превью показывает «— га», а операция пишется с `actual_area_ha = NULL`, и
 * план-факт по площади врёт с первого же ввода.
 */
export function initialDraft(
  fieldAreaHa: number | null,
  opts: { subzoneId?: string | null; subzoneAreaHa?: number | null } = {},
): SowDraft {
  const area = opts.subzoneId ? (opts.subzoneAreaHa ?? null) : fieldAreaHa;
  return {
    ...emptyDraft(),
    subzoneId: opts.subzoneId ?? null,
    contourAreaHa: area,
    areaHa: area,
  };
}

/**
 * Расхождение «по контуру» ↔ «засеяно фактически».
 *
 * Контур в поле идеальный, а сеялка прошла не везде. Разница обязана быть
 * ВИДНА, а не молча потеряться — иначе площадь посева и площадь операции
 * разъедутся, и никто не вспомнит почему.
 */
export function areaDelta(draft: SowDraft): number | null {
  if (draft.contourAreaHa == null || draft.areaHa == null) return null;
  return Math.round((draft.areaHa - draft.contourAreaHa) * 100) / 100;
}

/** Потребность в семенах, кг. Склад в v1 не трогаем — только показываем цифру. */
export function seedNeedKg(draft: SowDraft): number | null {
  if (draft.seedingNormKgHa == null || draft.areaHa == null) return null;
  return Math.round(draft.seedingNormKgHa * draft.areaHa * 10) / 10;
}

/** Сколько гектаров поля ещё свободно под посев. */
export function freeAreaHa(fieldAreaHa: number | null, occupiedHa: number | null): number | null {
  if (fieldAreaHa == null) return null;
  return Math.round((fieldAreaHa - (occupiedHa ?? 0)) * 100) / 100;
}

/**
 * Можно ли уйти с шага дальше.
 *
 * Мастер не пускает вперёд молча: у каждого «нельзя» есть причина словами
 * агронома, иначе кнопка «Дальше» просто не реагирует и это читается как баг.
 */
export function stepBlocker(step: SowStep, draft: SowDraft): string | null {
  if (step === 'where') {
    // Контур выбран, а площади нет — дальше пускать нельзя: без неё не
    // посчитать ни норму, ни семена, ни площадь операции «Сев».
    if (draft.contourAreaHa == null) {
      return 'У выбранного контура нет площади — выберите другой участок или укажите площадь руками.';
    }
    return null;
  }
  if (step === 'what') {
    if (draft.fallowKind) return null;
    if (!draft.cropKey) return 'Выберите культуру — без неё посев не заводится.';
    return null;
  }
  if (step === 'how') {
    if (draft.areaHa != null && draft.areaHa <= 0) {
      return 'Площадь посева должна быть больше нуля.';
    }
    if (draft.sowingDate == null) return 'Укажите дату сева — хотя бы ориентировочную.';
    return null;
  }
  // status
  if (draft.alreadySown && draft.fallowKind) {
    return 'Пар не сеют — уберите отметку «уже посеяли».';
  }
  if (draft.alreadySown && !draft.sowOperationTypeId) {
    return 'Выберите тип тех.операции «Сев» — она создастся вместе с посевом.';
  }
  return null;
}

/** Что реально произойдёт после «Сохранить» — текст показываем ДО нажатия. */
export function outcomeSummary(draft: SowDraft): string {
  if (draft.fallowKind) return 'Поле останется под паром — операций не создаётся.';
  if (!draft.alreadySown) {
    return 'Появится посев со статусом «Запланировано». Операций ноль — кнопка «Посеять» создаст операцию позже.';
  }
  return 'Появится посев со статусом «Растёт» и выполненная тех.операция «Сев» — без агроработ, технику и людей дозаполните позже.';
}

/** Подпись единицы густоты под полем ввода (меняется вместе с культурой). */
export function rateUnitHint(unit: SeedingRateUnit): string {
  return SEEDING_RATE_UNITS.find((u) => u.value === unit)?.hint ?? '';
}

/** Тело запроса `POST /fields/{id}/sow` из черновика мастера. */
export function toSowBody(draft: SowDraft, seasonId: string) {
  const cropId = draft.cropKey.startsWith('crop:') ? draft.cropKey.slice(5) : null;
  const lci = draft.cropKey && !draft.cropKey.startsWith('crop:') ? draft.cropKey : null;
  const fallow = Boolean(draft.fallowKind);
  return {
    season_id: seasonId,
    subzone_id: draft.subzoneId,
    crop_id: fallow ? null : cropId,
    local_catalog_item_id: fallow ? null : lci,
    fallow_kind: draft.fallowKind,
    variety_ids: draft.varietyIds,
    contour_area_ha: draft.contourAreaHa,
    area_ha: draft.areaHa,
    area_diff_reason: draft.areaDiffReason.trim() || null,
    sowing_date: draft.sowingDate,
    comment: draft.comment.trim() || null,
    already_sown: draft.alreadySown && !fallow,
    is_second_crop: draft.isSecondCrop,
    // Параметры сева уезжают в операцию — в посеве их нет (решение founder 07.08).
    sow_operation_type_id: draft.alreadySown && !fallow ? draft.sowOperationTypeId : null,
    seeding_rate: draft.seedingRate,
    seeding_rate_unit: draft.seedingRate != null ? draft.seedingRateUnit : null,
    seeding_norm_kg_ha: draft.seedingNormKgHa,
    seeding_depth_cm: draft.seedingDepthCm,
  };
}

/**
 * Какой тип тех.операции хозяйства предложить как «Сев».
 *
 * Сервер тип НЕ угадывает (у хозяйств «Сев», «Посев», «Сев зерновых»), но
 * форма обязана подставить очевидное, иначе агроном ищет строку в длинном
 * списке. Совпадение по началу слова, регистр не важен; не нашли — пусто,
 * пользователь выберет сам.
 */
export function guessSowOpType<T extends { id: string; local_name: string }>(
  types: ReadonlyArray<T>,
): T | null {
  const norm = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е');
  const exact = types.find((t) => norm(t.local_name) === 'сев');
  if (exact) return exact;
  const starts = types.find((t) => norm(t.local_name).startsWith('сев'));
  if (starts) return starts;
  return types.find((t) => norm(t.local_name).startsWith('посев')) ?? null;
}
